import { BaseApiService } from "./baseApiService";

/**
 * Individual filter option
 * Each object has one key representing the filter value
 */
export type FilterOption = Record<string, number>;

/**
 * Response structure for filter values API
 * Each field contains an array of filter options
 */
export interface FilterValuesResponse {
  policyCode: FilterOption[];
  documentLanguage: FilterOption[];
  documentType: FilterOption[];
  documentCategory: FilterOption[];
  documentSubCategory: FilterOption[];
}

/**
 * Service for fetching available filter values from the backend
 * Used to populate dynamic filter options in the UI
 */
export class FilterValuesService extends BaseApiService {
  /**
   * Get all available filter values for the current user's access level
   */
  async getFilterValues(): Promise<FilterValuesResponse> {
    const url = "/filter-values";
    try {
      // Use static method that properly handles smart API routing
      const response = await BaseApiService.get({
        url,
        responseDefaults: {},
      });

      if (response.success && response) {
        console.log("✅ Filter Values API Response:", response);
        return response as unknown as FilterValuesResponse;
      } else {
        console.error("❌ Filter Values API Error:", response.error);
        throw new Error(response.error || "Failed to get filter values");
      }
    } catch (error) {
      console.error("❌ Filter Values Service Error:", error);
      throw error;
    }
  }

  /**
   * Extract filter options as simple arrays for easy UI consumption
   */
  extractFilterOptions(filterValues: FilterValuesResponse | null | undefined): {
    policyCodes: string[];
    documentLanguages: string[];
    documentTypes: string[];
    documentCategories: string[];
    documentSubCategories: string[];
  } {
    const extractKeys = (filterOptions: FilterOption[] | undefined): string[] => {
      if (!filterOptions || !Array.isArray(filterOptions)) {
        return [];
      }
      return filterOptions.flatMap(option => Object.keys(option));
    };

    // Handle null/undefined filterValues
    if (!filterValues) {
      return {
        policyCodes: [],
        documentLanguages: [],
        documentTypes: [],
        documentCategories: [],
        documentSubCategories: [],
      };
    }

    return {
      policyCodes: extractKeys(filterValues.policyCode),
      documentLanguages: extractKeys(filterValues.documentLanguage),
      documentTypes: extractKeys(filterValues.documentType),
      documentCategories: extractKeys(filterValues.documentCategory),
      documentSubCategories: extractKeys(filterValues.documentSubCategory),
    };
  }
}

// Export singleton instance
export const filterValuesService = new FilterValuesService();
